import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './CookieConsent.css';

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

const RevisedCookieConsent: React.FC = () => {
  const [showBanner, setShowBanner] = useState<boolean>(false);
  const [showPreferences, setShowPreferences] = useState<boolean>(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true, // Always true and disabled
    analytics: true,
    marketing: false,
    preferences: true
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consentGiven = localStorage.getItem('cookieConsent');
    if (!consentGiven) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences
      try {
        const savedPreferences = JSON.parse(localStorage.getItem('cookiePreferences') || '');
        if (savedPreferences) {
          setPreferences(savedPreferences);
        }
      } catch (e) {
        // If parsing fails, we'll use the defaults
      }
    }
  }, []);

  // Set up listener for custom event to open preferences
  useEffect(() => {
    const handleOpenEvent = () => {
      setShowPreferences(true);
    };
    
    window.addEventListener('openCookiePreferences', handleOpenEvent);
    
    return () => {
      window.removeEventListener('openCookiePreferences', handleOpenEvent);
    };
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    };
    setPreferences(allAccepted);
    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('cookiePreferences', JSON.stringify(allAccepted));
    setShowBanner(false);
    setShowPreferences(false);
    
    // Here you would initialize your actual cookies/tracking based on preferences
    initializeCookies(allAccepted);
  };

  const handleAcceptSelected = () => {
    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
    setShowBanner(false);
    setShowPreferences(false);
    
    // Here you would initialize your actual cookies/tracking based on preferences
    initializeCookies(preferences);
  };

  const handlePreferenceChange = (category: keyof CookiePreferences) => {
    if (category === 'necessary') return; // Can't change necessary cookies
    
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleOpenPreferences = () => {
    setShowPreferences(true);
  };

  const initializeCookies = (cookiePreferences: CookiePreferences) => {
    // This is where you would initialize your actual tracking services
    console.log('Initializing cookies with preferences:', cookiePreferences);
  };

  if (!showBanner && !showPreferences) return null;

  // Simple banner at bottom
  return (
    <>
      {/* Basic Cookie Banner */}
      {showBanner && !showPreferences && (
        <div className="cookie-banner-simple">
          <div className="cookie-message">
            <p>
              We use cookies to enhance your experience. By continuing to visit this site you agree to our use of cookies.
            </p>
          </div>
          <div className="cookie-actions-simple">
            <button onClick={handleOpenPreferences} className="btn-customize">
              Customize
            </button>
            <button onClick={handleAcceptAll} className="btn-accept-all">
              Accept All
            </button>
          </div>
        </div>
      )}

      {/* Simple Modal for Preferences */}
      {showPreferences && (
        <div className="cookie-modal-overlay">
          <div className="cookie-modal">
            <div className="cookie-modal-header">
              <h2>Cookie Preferences</h2>
              <button 
                className="cookie-close-btn"
                onClick={() => setShowPreferences(false)}
              >
                ×
              </button>
            </div>
            
            <div className="cookie-modal-body">
              <p>
                We use cookies to help you navigate efficiently and perform certain functions. 
                You will find detailed information about all cookies under each consent category below.
              </p>
              
              <div className="cookie-category">
                <div className="cookie-category-header">
                  <div>
                    <h3>Necessary Cookies</h3>
                    <p>These cookies are required for the website to function and cannot be switched off.</p>
                  </div>
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={preferences.necessary} 
                      disabled 
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
              
              <div className="cookie-category">
                <div className="cookie-category-header">
                  <div>
                    <h3>Analytics Cookies</h3>
                    <p>These cookies allow us to measure traffic and analyze your behavior.</p>
                  </div>
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={preferences.analytics} 
                      onChange={() => handlePreferenceChange('analytics')} 
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
              
              <div className="cookie-category">
                <div className="cookie-category-header">
                  <div>
                    <h3>Marketing Cookies</h3>
                    <p>These cookies help us show you relevant advertisements based on your interests.</p>
                  </div>
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={preferences.marketing} 
                      onChange={() => handlePreferenceChange('marketing')} 
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
              
              <div className="cookie-category">
                <div className="cookie-category-header">
                  <div>
                    <h3>Preference Cookies</h3>
                    <p>These cookies remember your settings and preferences to enhance your experience.</p>
                  </div>
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={preferences.preferences} 
                      onChange={() => handlePreferenceChange('preferences')} 
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>
            
            <div className="cookie-modal-footer">
              <button 
                className="btn-save"
                onClick={handleAcceptSelected}
              >
                Save Preferences
              </button>
              <button 
                className="btn-accept-all"
                onClick={handleAcceptAll}
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RevisedCookieConsent;